import React from "react";

import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { closeConfirmModal, openConfirmModal } from "actions";

import { SaleStatusAction } from "actions/saleStatus";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { Button, DeleteButton, SecondaryButton } from "components/Form";
import { useSubmitHandler } from "components/Form/FormikControls";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { SaleStatusForm } from "components/SaleStatus/Form";

import { AuditLogTypes } from "constants/auditLog";
import { SaleStatusPermission } from "constants/permissions";

import { deepObjectChanges } from "lib/compare";
import { hasPermission } from "lib/permissions";

import { getSaleStatusById } from "selectors";

import { useCanAddSaleStatusPermission } from "hooks";

import { SaleStatus, SaleStatusArchetype } from "constants/sale";
import { SaleStatusValidationSchema } from "./validationSchema";

interface FooterProps {
  onClose: () => void;
  id: string | null;
}

export function Footer(props: FooterProps) {
  const { onClose, id } = props;
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false);

  const saleStatus = useSelector<RootState, SaleStatus>(getSaleStatusById(id));

  const hasCreatePermission = useCanAddSaleStatusPermission();

  const hasEditPermission: boolean = hasPermission(
    saleStatus,
    SaleStatusPermission.update,
  );

  const hasSavePermission: boolean = id
    ? hasEditPermission
    : hasCreatePermission;

  const hasDeletePermission: boolean = hasPermission(
    saleStatus,
    SaleStatusPermission.delete,
  );

  useSubmitHandler(false, setIsSubmitEnabled);
  const dispatch = useDispatch();
  const onClickConfirmDelete = () => {
    dispatch(SaleStatusAction.delete(id));
    dispatch(closeConfirmModal());
    onClose();
  };

  function onClickCancelDelete() {
    dispatch(closeConfirmModal());
  }

  function onClickDelete() {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message: `Deleting this sale status will move any sales in ${saleStatus.name} to the ${saleStatus.archetype} status. ${saleStatus.name} will no longer be selectable.`,
        actions: [
          {
            label: "Cancel",
            onClick: onClickCancelDelete,
            secondary: true,
          },
          {
            label: "Delete Sale Status",
            onClick: onClickConfirmDelete,
            secondary: false,
          },
        ],
      }),
    );
  }

  return (
    <>
      <SecondaryButton type="button" onClick={onClose}>
        Close
      </SecondaryButton>
      {saleStatus && (
        <DeleteButton
          type="button"
          disabled={!hasDeletePermission}
          onClick={onClickDelete}
        >
          Delete
        </DeleteButton>
      )}
      <Button type="submit" disabled={!isSubmitEnabled || !hasSavePermission}>
        Save
      </Button>
    </>
  );
}

interface SaleStatusModalProps {
  id: string | null;
  onClose: () => void;
  formDefaults: object;
}

interface SaleStatusFormValues {
  id: string;
  name: string;
  archetype: SaleStatusArchetype;
  saleType: string;
}

export function SaleStatusModal(props: SaleStatusModalProps) {
  const { id, onClose, formDefaults } = props;
  const dispatch = useDispatch();
  const saleStatus: SaleStatus = useSelector(getSaleStatusById(id));

  const initialValues: Partial<SaleStatusFormValues> = {
    archetype: SaleStatus.DRAFT,
    ...formDefaults,
    ...saleStatus,
  };

  function onSubmit(values: SaleStatusFormValues) {
    if (values.id) {
      dispatch(
        SaleStatusAction.update(
          { id, ...deepObjectChanges(initialValues, values) },
          { changeReason: "Updated from Edit Sale Status modal" },
        ),
      );
    } else {
      dispatch(SaleStatusAction.create(values));
    }
    onClose();
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle onClose={onClose}>
        {id && (
          <AuditLogLink
            auditLogType={AuditLogTypes.SALE_STATUS}
            dataId={id}
            returnTo={window.location.hash}
          />
        )}
        &nbsp;{id ? "Edit" : "Create"} Sale Status
        <AdminLink model="salestatus" cbid={id} />
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={SaleStatusValidationSchema}
      >
        <Form data-tour="sale-status-form">
          <DialogContent dividers>
            <SaleStatusForm />
          </DialogContent>
          <DialogActions>
            <Footer id={id} onClose={onClose} />
          </DialogActions>
        </Form>
      </Formik>
    </ZoomyDialog>
  );
}
